import { EditorStarterAsset } from '../../assets/assets';
import { EditorStarterItem } from '../../items/item-type';
import {
  getMaxTransitionDurationInFrames,
  TransitionItem,
  MIN_TRANSITION_DURATION_IN_FRAMES,
} from '../../items/transition/transition-item-type';
import {
  calculateTransitionExtension,
  calculateTransitionFrom,
} from '../../items/transition/transition-extension-utils';
import { EditorState } from '../types';

/**
 * 아이템이 source 또는 target인 transition들을 찾습니다.
 */
export const findTransitionsForItem = (
  transitions: Record<string, TransitionItem>,
  itemId: string,
): TransitionItem[] => {
  return Object.values(transitions).filter(
    (t) => t.sourceItemId === itemId || t.targetItemId === itemId,
  );
};

/**
 * Transition이 유효한지 확인합니다.
 * 유효 조건: sourceItem의 끝 === targetItem의 시작 (두 아이템이 정확히 인접)
 */
export const isTransitionValidForItems = (
  sourceItem: EditorStarterItem,
  targetItem: EditorStarterItem,
): boolean => {
  const sourceEnd = sourceItem.from + sourceItem.durationInFrames;
  return sourceEnd === targetItem.from;
};

interface RecalculateTransitionParams {
  transition: TransitionItem;
  sourceItem: EditorStarterItem;
  targetItem: EditorStarterItem;
  assets: Record<string, EditorStarterAsset>;
  fps: number;
}

/**
 * Transition의 from과 관련 속성들을 재계산합니다.
 * 아이템 duration이 transition을 수용할 수 없으면 null을 반환합니다.
 */
export const recalculateTransitionForItems = ({
  transition,
  sourceItem,
  targetItem,
  assets,
  fps,
}: RecalculateTransitionParams): TransitionItem | null => {
  // Transition duration은 두 아이템 중 작은 duration을 초과할 수 없음
  const maxDuration = getMaxTransitionDurationInFrames(
    transition,
    sourceItem,
    targetItem,
  );

  // 현재 transition duration이 최대값을 초과하면 조정
  let adjustedDuration = transition.durationInFrames;
  if (adjustedDuration > maxDuration) {
    adjustedDuration = maxDuration;
  }

  // 조정된 duration이 최소값보다 작으면 transition 삭제 대상
  if (adjustedDuration < MIN_TRANSITION_DURATION_IN_FRAMES) {
    return null;
  }

  // 새로운 경계점 및 from 계산
  const edgeFrame = sourceItem.from + sourceItem.durationInFrames;
  const newFrom =
    transition.transitionType === 'capsuleCover'
      ? edgeFrame - adjustedDuration
      : calculateTransitionFrom(edgeFrame, adjustedDuration);

  // 확장 정보 및 frozen frames 통합 계산
  const {
    sourceExtendedFrames,
    targetExtendedFrames,
    targetExtendedAssetStartDelta,
    sourceFrozenFrames,
    targetFrozenFrames,
  } = calculateTransitionExtension({
    sourceItem,
    targetItem,
    assets,
    fps,
    transitionFrom: newFrom,
    transitionDuration: adjustedDuration,
  });

  return {
    ...transition,
    from: newFrom,
    durationInFrames: adjustedDuration,
    sourceFrozenFrames,
    targetFrozenFrames,
    sourceExtendedFrames,
    targetExtendedFrames,
    targetExtendedAssetStartDelta,
  };
};

/**
 * 아이템 변경 후 연결된 transition들을 동기화합니다.
 *
 * 처리 내용:
 * 1. 해당 아이템과 연결된 모든 transition 찾기
 * 2. 두 아이템이 더 이상 인접하지 않으면 transition 삭제
 * 3. 유효한 transition은 from, frozenFrames 등 재계산
 */
export const syncTransitionsForItem = ({
  state,
  itemId,
}: {
  state: EditorState;
  itemId: string;
}): EditorState => {
  const { transitions, items, assets, fps } = state.undoableState;
  const relatedTransitions = findTransitionsForItem(transitions, itemId);

  if (relatedTransitions.length === 0) {
    return state;
  }

  const newTransitions = { ...transitions };
  let hasChanges = false;

  for (const transition of relatedTransitions) {
    const sourceItem = items[transition.sourceItemId];
    const targetItem = items[transition.targetItemId];

    // 아이템이 삭제된 경우 transition 삭제
    if (!sourceItem || !targetItem) {
      delete newTransitions[transition.id];
      hasChanges = true;
      continue;
    }

    // 두 아이템이 더 이상 인접하지 않으면 transition 삭제
    if (!isTransitionValidForItems(sourceItem, targetItem)) {
      delete newTransitions[transition.id];
      hasChanges = true;
      continue;
    }

    // 유효한 transition은 재계산
    const updatedTransition = recalculateTransitionForItems({
      transition,
      sourceItem,
      targetItem,
      assets,
      fps,
    });

    // 재계산 결과가 null이면 (아이템 duration이 너무 작아서 transition을 수용 불가) 삭제
    if (updatedTransition === null) {
      delete newTransitions[transition.id];
      hasChanges = true;
      continue;
    }

    // 변경사항이 있으면 업데이트
    if (
      transition.from !== updatedTransition.from ||
      transition.durationInFrames !== updatedTransition.durationInFrames ||
      transition.sourceFrozenFrames !== updatedTransition.sourceFrozenFrames ||
      transition.targetFrozenFrames !== updatedTransition.targetFrozenFrames ||
      transition.sourceExtendedFrames !==
        updatedTransition.sourceExtendedFrames ||
      transition.targetExtendedFrames !==
        updatedTransition.targetExtendedFrames ||
      transition.targetExtendedAssetStartDelta !==
        updatedTransition.targetExtendedAssetStartDelta
    ) {
      newTransitions[transition.id] = updatedTransition;
      hasChanges = true;
    }
  }

  if (!hasChanges) {
    return state;
  }

  return {
    ...state,
    undoableState: {
      ...state.undoableState,
      transitions: newTransitions,
    },
  };
};
